import * as O from 'fp-ts/lib/Option';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {
  ValidTaxIdentificationNumber,
  ExistingTaxIdentificationNumber,
  TaxIdentificationNumber,
  ValidatingTaxIdentificationNumber,
  TaxIdentificationNumberWithNoAgentNumbers,
} from 'Users/models/User/Agent/TaxIdentificationNumber';
import {AgentNumber} from 'Users/models/User/Agent/AgentNumber';
import {UnvalidatedTaxIdentificationNumber} from 'Agent/Policies/models/TaxIdentificationNumber';
import {SupportAgentPermissions, DefaultSupportAgentPermissions} from 'Users/models/User/Agent/SupportAgentPermissions';

export class UnvalidatedSupportAgencyAssociation {
  public readonly key = 'UnvalidatedSupportAgencyAssociation';

  constructor(
    public readonly taxId: TaxIdentificationNumber,
    public readonly supportAgentPermissions: SupportAgentPermissions,
    public readonly agentNumbers: AgentNumber[]
  ) {}
}

export class InvalidSupportAgencyAssociation {
  public readonly key = 'InvalidSupportAgencyAssociation';

  constructor(
    public readonly taxId: TaxIdentificationNumber,
    public readonly supportAgentPermissions: SupportAgentPermissions,
    public readonly agentNumbers: AgentNumber[]
  ) {}
}

export class ValidSupportAgencyAssociation {
  public static create(
    taxId: TaxIdentificationNumber,
    supportAgentPermissions: SupportAgentPermissions,
    agentNumbers: AgentNumber[]
  ) {
    const validatedAgentNumbers = NEA.fromArray(agentNumbers);
    const hasAtLeastOneAccess =
      supportAgentPermissions.canViewAccounts ||
      supportAgentPermissions.canViewClaims ||
      supportAgentPermissions.canViewCommissionsAnd1099s ||
      supportAgentPermissions.canViewCorrespondence ||
      supportAgentPermissions.canViewPolicies ||
      supportAgentPermissions.canViewPolicyServiceRequest ||
      supportAgentPermissions.canViewPendingUnderwritingRequirements;

    return (taxId instanceof ValidTaxIdentificationNumber || taxId instanceof ExistingTaxIdentificationNumber) &&
      O.isSome(validatedAgentNumbers) &&
      hasAtLeastOneAccess
      ? new ValidSupportAgencyAssociation(taxId, supportAgentPermissions, validatedAgentNumbers.value)
      : new InvalidSupportAgencyAssociation(taxId, supportAgentPermissions, agentNumbers);
  }

  public readonly key = 'ValidSupportAgencyAssociation';
  private constructor(
    public readonly taxId: ValidTaxIdentificationNumber | ExistingTaxIdentificationNumber,
    public readonly supportAgentPermissions: SupportAgentPermissions,
    public readonly agentNumbers: NEA.NonEmptyArray<AgentNumber>
  ) {}
}

export const loadTaxIdentificationNumber = (
  taxId: ValidTaxIdentificationNumber | TaxIdentificationNumberWithNoAgentNumbers
) => ({supportAgentPermissions, agentNumbers}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(taxId, supportAgentPermissions, agentNumbers);

export const changeCanViewAccounts = (canViewAccounts: boolean) => ({
  agentNumbers,
  supportAgentPermissions,
  taxId,
}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(taxId, {...supportAgentPermissions, canViewAccounts}, agentNumbers);

export const changeCanViewPolicies = (canViewPolicies: boolean) => ({
  agentNumbers,
  supportAgentPermissions,
  taxId,
}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(
    taxId,
    {
      ...supportAgentPermissions,
      canViewClaims: canViewPolicies && supportAgentPermissions.canViewClaims,
      canViewPolicies,
    },
    agentNumbers
  );

export const changeCanViewClaims = (canViewClaims: boolean) => ({
  agentNumbers,
  supportAgentPermissions,
  taxId,
}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(taxId, {...supportAgentPermissions, canViewClaims}, agentNumbers);

export const changeCanViewCommissionsAnd1099s = (canViewCommissionsAnd1099s: boolean) => ({
  agentNumbers,
  supportAgentPermissions,
  taxId,
}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(taxId, {...supportAgentPermissions, canViewCommissionsAnd1099s}, agentNumbers);

export const changeCanViewPolicyServiceRequest = (canViewPolicyServiceRequest: boolean) => ({
  agentNumbers,
  supportAgentPermissions,
  taxId,
}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(taxId, {...supportAgentPermissions, canViewPolicyServiceRequest}, agentNumbers);

export const changeCanViewCorrespondence = (canViewCorrespondence: boolean) => ({
  agentNumbers,
  supportAgentPermissions,
  taxId,
}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(taxId, {...supportAgentPermissions, canViewCorrespondence}, agentNumbers);

export const changeCanViewPendingUnderwritingRequirements = (canViewPendingUnderwritingRequirements: boolean) => ({
  agentNumbers,
  supportAgentPermissions,
  taxId,
}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(
    taxId,
    {...supportAgentPermissions, canViewPendingUnderwritingRequirements},
    agentNumbers
  );

export const changeTaxIdentificationNumber = (taxId: string) => ({
  supportAgentPermissions,
  agentNumbers,
}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(
    ValidatingTaxIdentificationNumber.create(taxId),
    supportAgentPermissions,
    agentNumbers
  );

export const addAgentNumber = (agentNumber: AgentNumber) => ({
  taxId,
  supportAgentPermissions,
  agentNumbers,
}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(taxId, supportAgentPermissions, [...agentNumbers, agentNumber]);

export const removeAgentNumber = (agentNumber: AgentNumber) => ({
  taxId,
  supportAgentPermissions,
  agentNumbers,
}: SupportAgencyAssociation) =>
  ValidSupportAgencyAssociation.create(
    taxId,
    supportAgentPermissions,
    agentNumbers.filter(an => an.value !== agentNumber.value)
  );

export type SupportAgencyAssociation =
  | ValidSupportAgencyAssociation
  | InvalidSupportAgencyAssociation
  | UnvalidatedSupportAgencyAssociation;

export type SupportAgencyAssociationOptions<TReturn> = ToMatchOptions<SupportAgencyAssociation, 'key', TReturn>;

export const matchSupportAgencyAssociation = <TReturn>(
  agencyAssociation: SupportAgencyAssociation,
  options: SupportAgencyAssociationOptions<TReturn>
): TReturn => {
  switch (agencyAssociation.key) {
    case 'InvalidSupportAgencyAssociation':
      return options.InvalidSupportAgencyAssociation(agencyAssociation);
    case 'UnvalidatedSupportAgencyAssociation':
      return options.UnvalidatedSupportAgencyAssociation(agencyAssociation);
    case 'ValidSupportAgencyAssociation':
      return options.ValidSupportAgencyAssociation(agencyAssociation);
  }
};

export const EmptySupportAgencyAssociation = new UnvalidatedSupportAgencyAssociation(
  new UnvalidatedTaxIdentificationNumber(''),
  DefaultSupportAgentPermissions,
  []
);
